import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { 
  ArrowRight, Plus, Trash2, Save, RotateCcw, 
  Facebook, Linkedin, Target, AlertCircle, CheckCircle2
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORM_ICONS = {
  meta: Facebook,
  linkedin: Linkedin,
  google_ads: Target
};

const PLATFORM_NAMES = {
  meta: 'Meta (Facebook/Instagram)',
  linkedin: 'LinkedIn Ads',
  google_ads: 'Google Ads'
};

const LeadFormMappingDialog = ({ open, onOpenChange, platform }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mappings, setMappings] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [platformFields, setPlatformFields] = useState([]);
  const [crmFields, setCrmFields] = useState([]);
  const [isDefault, setIsDefault] = useState(true);

  const PlatformIcon = PLATFORM_ICONS[platform] || Target;

  // Fetch mappings and fields
  const fetchMappings = useCallback(async () => {
    if (!platform) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/settings/lead-form-mappings/${platform}?token=${token}`
      );
      
      if (response.data.success) {
        const data = response.data.mappings;
        setMappings(data.mappings || []);
        setCustomFields(data.custom_fields || []);
        setIsDefault(data.is_default || false);
        setPlatformFields(response.data.platform_fields || []);
        setCrmFields(response.data.crm_fields || []);
      }
    } catch (error) {
      console.error('Failed to fetch mappings:', error);
      toast.error('Failed to load field mappings');
    } finally {
      setLoading(false);
    }
  }, [platform, token]);

  useEffect(() => {
    if (open && platform) {
      fetchMappings();
    }
  }, [open, platform, fetchMappings]);

  // Add new mapping
  const addMapping = () => {
    setMappings([...mappings, { source_field: '', target_field: '' }]);
  };

  // Update mapping
  const updateMapping = (index, field, value) => {
    const updated = [...mappings];
    updated[index] = { ...updated[index], [field]: value };
    setMappings(updated);
  };

  // Remove mapping
  const removeMapping = (index) => {
    setMappings(mappings.filter((_, i) => i !== index));
  };

  // Add custom field
  const addCustomField = () => {
    setCustomFields([...customFields, { source_field: '', target_field: '', label: '' }]);
  };

  // Update custom field
  const updateCustomField = (index, field, value) => {
    const updated = [...customFields];
    updated[index] = { ...updated[index], [field]: value };
    setCustomFields(updated);
  };

  // Remove custom field
  const removeCustomField = (index) => {
    setCustomFields(customFields.filter((_, i) => i !== index));
  };

  // Save mappings
  const saveMappings = async () => {
    // Validate mappings
    const validMappings = mappings.filter(m => m.source_field && m.target_field);
    const validCustomFields = customFields.filter(cf => cf.source_field && cf.target_field);

    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/settings/lead-form-mappings/${platform}?token=${token}`,
        { mappings: validMappings, custom_fields: validCustomFields }
      );

      if (response.data.success) {
        toast.success('Field mappings saved successfully');
        setIsDefault(false);
        onOpenChange(false);
      }
    } catch (error) {
      console.error('Failed to save mappings:', error);
      toast.error('Failed to save field mappings');
    } finally {
      setSaving(false);
    }
  };

  // Reset to defaults
  const resetToDefaults = async () => {
    setSaving(true);
    try {
      const response = await axios.post(
        `${API}/settings/lead-form-mappings/${platform}/reset?token=${token}`
      );

      if (response.data.success) {
        setMappings(response.data.mappings || []);
        setCustomFields([]);
        setIsDefault(true);
        toast.success('Mappings reset to defaults');
      }
    } catch (error) {
      console.error('Failed to reset mappings:', error);
      toast.error('Failed to reset mappings');
    } finally {
      setSaving(false);
    }
  };

  // Get unmapped platform fields
  const getUnmappedPlatformFields = () => {
    const mappedSources = new Set(mappings.map(m => m.source_field));
    return platformFields.filter(f => !mappedSources.has(f.key));
  };

  // Get unmapped CRM fields
  const getUnmappedCrmFields = () => {
    const mappedTargets = new Set(mappings.map(m => m.target_field));
    return crmFields.filter(f => !mappedTargets.has(f.key));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PlatformIcon className="h-5 w-5" />
            Lead Form Field Mapping - {PLATFORM_NAMES[platform]}
          </DialogTitle>
          <DialogDescription>
            Map incoming lead form fields to your CRM contact fields. This ensures lead data is correctly stored.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6">
              {/* Status Badge */}
              <div className="flex items-center gap-2">
                {isDefault ? (
                  <Badge variant="secondary" className="gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Using Default Mappings
                  </Badge>
                ) : (
                  <Badge variant="default" className="gap-1 bg-green-600">
                    <CheckCircle2 className="h-3 w-3" />
                    Custom Mappings Saved
                  </Badge>
                )}
              </div>

              {/* Standard Mappings */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Field Mappings</Label>
                  <Button variant="outline" size="sm" onClick={addMapping}>
                    <Plus className="h-4 w-4 mr-1" /> Add Mapping
                  </Button>
                </div>

                {/* Header */}
                <div className="grid grid-cols-[1fr,40px,1fr,40px] gap-2 px-2 py-1 bg-muted rounded text-sm font-medium">
                  <span>{PLATFORM_NAMES[platform]} Field</span>
                  <span></span>
                  <span>CRM Field</span>
                  <span></span>
                </div>

                {/* Mapping Rows */}
                {mappings.map((mapping, index) => (
                  <div key={index} className="grid grid-cols-[1fr,40px,1fr,40px] gap-2 items-center">
                    <Select
                      value={mapping.source_field}
                      onValueChange={(value) => updateMapping(index, 'source_field', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select source field" />
                      </SelectTrigger>
                      <SelectContent>
                        {platformFields.map((field) => (
                          <SelectItem key={field.key} value={field.key}>
                            {field.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <div className="flex justify-center">
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </div>

                    <Select
                      value={mapping.target_field}
                      onValueChange={(value) => updateMapping(index, 'target_field', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select CRM field" />
                      </SelectTrigger>
                      <SelectContent>
                        {crmFields.map((field) => (
                          <SelectItem key={field.key} value={field.key}>
                            {field.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeMapping(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                {mappings.length === 0 && (
                  <div className="text-center py-4 text-muted-foreground">
                    No mappings configured. Click "Add Mapping" to start.
                  </div>
                )}
              </div>

              <Separator />

              {/* Custom Fields Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-base font-semibold">Custom Field Mappings</Label>
                    <p className="text-sm text-muted-foreground">
                      Map custom form questions to CRM fields
                    </p>
                  </div>
                  <Button variant="outline" size="sm" onClick={addCustomField}>
                    <Plus className="h-4 w-4 mr-1" /> Add Custom
                  </Button>
                </div>

                {customFields.map((field, index) => (
                  <div key={index} className="grid grid-cols-[1fr,40px,1fr,40px] gap-2 items-center">
                    <Input
                      placeholder="Custom field name (exact match)"
                      value={field.source_field}
                      onChange={(e) => updateCustomField(index, 'source_field', e.target.value)}
                    />

                    <div className="flex justify-center">
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </div>

                    <Select
                      value={field.target_field}
                      onValueChange={(value) => updateCustomField(index, 'target_field', value)}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select CRM field" />
                      </SelectTrigger>
                      <SelectContent>
                        {crmFields.map((f) => (
                          <SelectItem key={f.key} value={f.key}>
                            {f.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeCustomField(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}

                {customFields.length === 0 && (
                  <div className="text-center py-2 text-sm text-muted-foreground">
                    No custom fields. Use this for custom form questions.
                  </div>
                )}
              </div>

              <Separator />

              {/* Unmapped Fields Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">
                    Unmapped {PLATFORM_NAMES[platform]} Fields
                  </Label>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {getUnmappedPlatformFields().slice(0, 8).map((field) => (
                      <Badge key={field.key} variant="outline" className="text-xs">
                        {field.label}
                      </Badge>
                    ))}
                    {getUnmappedPlatformFields().length > 8 && (
                      <Badge variant="outline" className="text-xs">
                        +{getUnmappedPlatformFields().length - 8} more
                      </Badge>
                    )}
                  </div>
                </div>
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">
                    Unmapped CRM Fields
                  </Label>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {getUnmappedCrmFields().slice(0, 8).map((field) => (
                      <Badge key={field.key} variant="outline" className="text-xs">
                        {field.label}
                      </Badge>
                    ))}
                    {getUnmappedCrmFields().length > 8 && (
                      <Badge variant="outline" className="text-xs">
                        +{getUnmappedCrmFields().length - 8} more
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
        )}

        <DialogFooter className="flex justify-between sm:justify-between">
          <Button
            variant="outline"
            onClick={resetToDefaults}
            disabled={saving || isDefault}
          >
            <RotateCcw className="h-4 w-4 mr-2" />
            Reset to Defaults
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={saveMappings} disabled={saving}>
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Mappings
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LeadFormMappingDialog;
